/**
 * EmbeddedEntryBlock Component
 *
 * Bridge component for rendering embedded Contentful entries within Rich Text.
 * This component is used by jsxParser to convert placeholder divs into actual components.
 *
 * The placeholder HTML: <div role="embedded-entry-block" data-id="..." data-component="..."></div>
 * is converted by jsxParser using RAW_COMPONENTS mapping.
 */

import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/contentful/app/components/contentful/component';

/**
 * Parse component data from data-component attribute
 *
 * @param {string} dataComponent - JSON string of component data
 * @returns {Object|null}
 */
const parseComponentData = (dataComponent) => {
  if (!dataComponent) return null;

  try {
    return JSON.parse(dataComponent);
  } catch (error) {
    console.error('[EmbeddedEntryBlock] Failed to parse component data:', error);

    return null;
  }
};

/**
 * EmbeddedEntryBlock Component
 *
 * Renders an embedded Contentful entry using the Component wrapper.
 * The Component wrapper will auto-fetch full data if needed.
 *
 * @param {Object} props - Component props
 * @param {string} props.data-id - Entry ID
 * @param {string} props.data-component - JSON string of component data
 */
export const EmbeddedEntryBlock = ({'data-id': dataId, 'data-component': dataComponent, ...props}) => {
  const component = parseComponentData(dataComponent);

  // If no valid component data or entry is draft/unpublished, hide block (no placeholder)
  // This can happen when links query fails or entry is not published
  if (!component || !component.__typename || !component.sys?.id) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[EmbeddedEntryBlock] Missing data for entry: ${dataId}. Hiding draft/unpublished embed.`);
    }

    return null;
  }

  return (
    <Box className="embedded-entry-block" my={6} {...props}>
      <Component component={component} />
    </Box>
  );
};

EmbeddedEntryBlock.displayName = 'EmbeddedEntryBlock';
EmbeddedEntryBlock.propTypes = {
  'data-id': PropTypes.string,
  'data-component': PropTypes.string,
};

export default EmbeddedEntryBlock;
